// src/services/ingestion/shared.js
// Shared utilities for all ingestion pipeline modules.
// proxyFetch is the single network entry point for all external APIs.

import { CORS_PROXIES, TIMEOUT } from '@config';
import { captureNetworkFailure } from '@services/monitoring';

// ── CORS proxy fetch ──────────────────────────────────────────────
// Tries each configured CORS proxy in sequence.
// Returns parsed JSON. Throws only when ALL proxies fail.
// All ingestion modules use this — never call fetch() directly.
export async function proxyFetch(url, timeoutMs = TIMEOUT.alerts) {
  let lastErr;
  for (const proxy of CORS_PROXIES) {
    try {
      const res  = await fetch(proxy + encodeURIComponent(url), {
        signal: AbortSignal.timeout(timeoutMs),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // allorigins.win wraps response in { contents: "..." }
      // corsproxy.io returns the raw response
      const text = json.contents ?? json.body ?? JSON.stringify(json);
      return typeof text === 'string' ? JSON.parse(text) : text;
    } catch (err) {
      lastErr = err;
    }
  }
  captureNetworkFailure(url, 'all_proxies_failed', CORS_PROXIES.length);
  throw new Error(`All proxies failed for ${url}: ${lastErr?.message}`);
}

// ── Direct fetch (for endpoints that support CORS natively) ──────
// NWS API, NOAA Tides, and USGS all send CORS headers.
// Use this instead of proxyFetch when the endpoint allows direct access.
export async function directFetch(url, timeoutMs = TIMEOUT.alerts) {
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  } catch (err) {
    // Fall back to proxy if direct fetch fails (could be CORS in some browsers)
    return proxyFetch(url, timeoutMs);
  }
}

// ── Adapter Result envelope ───────────────────────────────────────
// Every ingestion adapter should return this shape so callers can
// distinguish "no data" from "fetch failed" from "stale cache".
//
// Usage:
//   return adapterOk(alerts, { source: 'nws', fetchedAt: now })
//   return adapterError('fetch_failed', err.message, cachedData)
//   return adapterStale(cachedData, { source: 'nws', cachedAt })
//
// Shape:
//   { ok, data, status, source, fetchedAt, error, fromCache }
//
// status: 'live' | 'stale' | 'error' | 'empty'

export function adapterOk(data, { source = 'unknown', fetchedAt = new Date().toISOString() } = {}) {
  return {
    ok:        true,
    data:      data ?? [],
    status:    data?.length === 0 ? 'empty' : 'live',
    source,
    fetchedAt,
    fromCache: false,
    error:     null,
  };
}

export function adapterError(code, message, cachedData = null, { source = 'unknown' } = {}) {
  return {
    ok:        false,
    data:      cachedData ?? [],
    status:    cachedData ? 'stale' : 'error',
    source,
    fetchedAt: null,
    fromCache: !!cachedData,
    error:     { code, message },
  };
}

export function adapterStale(data, { source = 'unknown', cachedAt = null } = {}) {
  return {
    ok:        true,
    data:      data ?? [],
    status:    'stale',
    source,
    fetchedAt: cachedAt,
    fromCache: true,
    error:     null,
  };
}
